import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { API } from "@/lib/api";

// On-screen only: pages are images, there is no PDF to download or print.
const KINDS = {
  organization: { label: "Sample Organizational Authority & Culture Portrait · fictional Organization X", back: "/#organizations" },
  individual: { label: "Sample Authority Portrait · illustrative participant", back: "/#individuals" },
};

export default function SampleOrgReport({ kind = "organization" }) {
  const cfg = KINDS[kind];
  const [count, setCount] = useState(0);
  const [err, setErr] = useState(false);
  const [current, setCurrent] = useState(1);
  useEffect(() => {
    fetch(`${API}/public/sample/${kind}/pages`).then((r) => r.json()).then((d) => setCount(d.count)).catch(() => setErr(true));
  }, [kind]);
  useEffect(() => {
    if (!count) { return undefined; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setCurrent(Number(e.target.dataset.page)); } });
    }, { rootMargin: "-45% 0px -50% 0px" });
    document.querySelectorAll("[data-page]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [count]);
  const jump = (n) => document.getElementById(`sample-p${n}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const block = (e) => e.preventDefault();
  return (
    <div className="cg-landing min-h-screen bg-[#ECEAE4]" onContextMenu={block} data-testid="sample-org-viewer">
      <style>{"@media print { body { display: none !important; } }"}</style>
      <header className="cg-landing-header sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to={cfg.back} data-testid="sample-back" className="inline-flex items-center gap-2 text-sm text-[#17324D] font-medium"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <p className="text-xs sm:text-sm text-[#56606B] text-right">{cfg.label}</p>
        </div>
        {count > 0 && <PageJump count={count} current={current} onJump={jump} />}
      </header>
      <div className="max-w-4xl mx-auto px-3 sm:px-8 py-8 space-y-5">
        {err && <p className="text-center text-[#56606B]" data-testid="sample-error">The sample couldn't load. Please try again shortly.</p>}
        {!count && !err && <p className="text-center text-[#56606B] animate-pulse">Loading sample…</p>}
        {count > 0 && <p className="text-center text-xs text-[#56606B] sm:hidden" data-testid="sample-zoom-hint">Pinch to zoom for easier reading.</p>}
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} id={`sample-p${i + 1}`} data-page={i + 1} className="scroll-mt-32 relative w-full aspect-[1105/1430] bg-[#F4F2EC] shadow-md rounded-sm overflow-hidden" data-testid={`sample-frame-${i + 1}`}>
            <span className="absolute inset-0 flex items-center justify-center text-sm text-[#8A8F98] animate-pulse">Loading page {i + 1}…</span>
          <img src={`${API}/public/sample/${kind}/page/${i + 1}.jpg`} alt={`Sample report page ${i + 1}`} loading={i < 2 ? "eager" : "lazy"} width={1105} height={1430}
            decoding="async" fetchpriority={i === 0 ? "high" : "auto"}
            draggable={false} onDragStart={block} data-testid={`sample-page-${i + 1}`}
            className="relative w-full h-full select-none pointer-events-none" />
          </div>
        ))}
        {count > 0 && <p className="text-center text-xs text-[#56606B] pt-2">Illustrative example based on fictional aggregate results. Available to view on screen only.</p>}
      </div>
    </div>
  );
}

function PageJump({ count, current, onJump }) {
  return (
    <nav className="border-t border-[#17324D]/10 bg-[#ECEAE4]/95 backdrop-blur" aria-label="Jump to page" data-testid="sample-page-jump">
      <div className="max-w-4xl mx-auto px-3 sm:px-8 py-2 flex items-center gap-1.5 overflow-x-auto">
        <span className="text-[11px] uppercase tracking-wider text-[#56606B] mr-1 shrink-0">Page</span>
        {Array.from({ length: count }).map((_, i) => {
          const n = i + 1;
          const on = n === current;
          return (
            <button key={n} type="button" onClick={() => onJump(n)} aria-current={on ? "page" : undefined} data-testid={`sample-jump-${n}`}
              className={`shrink-0 w-7 h-7 rounded-full text-xs font-medium transition-colors ${on ? "bg-[#17324D] text-white" : "text-[#17324D] hover:bg-[#17324D]/10"}`}>
              {n}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
